import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import uuid from 'short-unique-id';
import { IColumnSettings, ICustomColumn } from '../../shared/shared/table/components/custom-columns-settings/column-settings';
import { IExportCSVPreview } from '../contract-control/modals/export-csv-modal/IExport-csv-preview';
import { DocumentColumnsLabelTranslations } from '../settings/documenttype/document-type-settings-columns';
import { IDocumentListingDto, IDocumentTypesDto, IDocumentTypesListingDto } from './dto/document-listing.dto';
import {
    DocumentViewVariantEnum, IAdvancedSearchRequest, IDocumentPreviewRequest, IExportDocumentsRequest
} from './store/document-listing.reducers';


export interface IGetDocumentCustomColumns {
    column_settings: ColumnSettings;
    created_at: string;
    id: number;
    updated_at: string;
    user_id: number;
    workspace_id: number;
}

export interface ColumnSettings {
    column1: string;
    column2: string;

}

@Injectable({
    providedIn: 'root'
})
export class DocumentListingApiService {
    private uuid = new uuid({ length: 6, dictionary: 'number' });

    constructor(private httpClient: HttpClient,
        private transloco: TranslocoService
    ) {
    }

    getDocuments(viewVariant: DocumentViewVariantEnum, queryParams?: Partial<IAdvancedSearchRequest>): Observable<IDocumentListingDto> {
        return this.getDocumentsPost(viewVariant, queryParams);
    }

    getDocumentsTypes(): Observable<IDocumentTypesDto[]> {
        return this.httpClient.get<IDocumentTypesDto[]>('documenttypes?p=all&document_type_data=true'); // (p) parameter will get all records
    }

    deleteDocuments(documentsIds: number[]) {
        return this.httpClient.delete<IDocumentTypesListingDto>('trashed-documents/bulk-delete', {
            body: {
                documents: documentsIds
            }
        });
    }

    restoreDocuments(documentsIds: number[]) {
        return this.httpClient.put<IDocumentTypesListingDto>('trashed-documents/bulk-restore', {
            documents: documentsIds
        });
    }

    getDefaultDocumentColumns() {
        return [
            {
                order: 0,
                uuid: this.uuid(),
                cid: 'documenttype-001',
                doc_id: null,
                field_name: 'document_type',
                label: DocumentColumnsLabelTranslations.document_type,
                status: 1,
                type: 'default'
            },
            {
                order: 1,
                uuid: this.uuid(),
                cid: 'template-name-001',
                doc_id: null,
                field_name: 'template_name',
                label: DocumentColumnsLabelTranslations.template_name,
                status: 1,
                type: 'default'
            },
            {
                order: 2,
                uuid: this.uuid(),
                cid: 'user-002',
                doc_id: null,
                field_name: 'user',
                label: DocumentColumnsLabelTranslations.user,
                status: 1,
                type: 'default'
            },
            {
                order: 3,
                uuid: this.uuid(),
                cid: 'recipientroles-003',
                doc_id: null,
                field_name: 'recipient_roles',
                label: DocumentColumnsLabelTranslations.recipient_roles,
                status: 1,
                type: 'default'
            },
            {
                order: 4,
                uuid: this.uuid(),
                cid: 'organization-003',
                doc_id: null,
                field_name: 'organization',
                label: DocumentColumnsLabelTranslations.organization,
                status: 1,
                type: 'default'
            },
            {
                order: 5,
                uuid: this.uuid(),
                cid: 'statusupdatedat-005',
                doc_id: null,
                field_name: 'status_updated_at',
                label: DocumentColumnsLabelTranslations.status_updated_at,
                status: 1,
                type: 'default'
            }

        ];
    }

    getCustomColumns(type?: string): Observable<ICustomColumn> {
        const defaultsColumns = this.getDefaultDocumentColumns();

        if (type === 'getDefault') {
            return of({
                column_settings: defaultsColumns,
                created_at: new Date(),
                id: 1,
                order_settings: defaultsColumns,
                updated_at: new Date(),
                user_id: 1,
                workspace_id: 1
            })
        } else {
            return this.httpClient.get<IGetDocumentCustomColumns>('documents/custom-columns')
                .pipe(
                    map((response) => {
                        return JSON.parse(JSON.stringify(this.fixColumnLabels(response, defaultsColumns)));
                    }),
                    catchError(() => {
                        return of({
                            column_settings: defaultsColumns,
                            created_at: new Date(),
                            id: 1,
                            order_settings: defaultsColumns,
                            updated_at: new Date(),
                            user_id: 1,
                            workspace_id: 1
                        })
                    })
                )
        }
    }

    fixColumnLabels(fetchColumnsResponse, defaultsColumns) {
        fetchColumnsResponse.column_settings.forEach(element => {
            const existingLabel = defaultsColumns.find(x => x.field_name == element.field_name)?.label;
            if (existingLabel) {
                element.label = existingLabel;
            }
        })

        return fetchColumnsResponse;
    }


    updateCustomColumns(column_settings: IColumnSettings[]) {
        return this.httpClient.post<{ column_settings: IColumnSettings[] }>('documents/custom-columns', { column_settings: column_settings });
    }

    exportPreview(params: IDocumentPreviewRequest): Observable<IExportCSVPreview<IDocumentPreviewRequest>> {
        return this.httpClient.post<IExportCSVPreview<IDocumentPreviewRequest>>('export/preview/documents', params);
    }

    exportDocuments(exportOptions: IExportDocumentsRequest) {
        return this.httpClient.post('export/documents', exportOptions);
    }

    deleteDocument(documentId: number) {
        return this.httpClient.delete(`documents/${documentId}`);
    }

    getDocumentsPost(viewVariant: DocumentViewVariantEnum, params: Partial<IAdvancedSearchRequest>): Observable<IDocumentListingDto> {
        const endpoint = viewVariant === DocumentViewVariantEnum.trash_view ? 'trashed-documents-search' : 'documents-search';

        return this.httpClient.post<IDocumentListingDto>(endpoint, params);
    }

    retrySendingToMetodika(documentId: number): Observable<{ message: string }> {
        return this.httpClient.post<{ message: string }>(`documents/${documentId}/retry-sending-to-metodika`, {});
    }
}

